import { useFormContext, useWatch } from 'react-hook-form';
import { FormPercentageInputField, FormSelectionCardGroupField, lt, useLocalizeProperty } from '@mews/framework';
import { ContentSection, createOption } from '@mews-commander/ui';
import { AmountPart, ProductPricing, useRepositoryQuery } from '@mews-commander/data-access';
import { Disableable, Stack, StackItem } from '@optimus-web/core';
import { CurrencyInputField, EnumSelectField, SelectField } from '@mews-commander/form';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { CurrencySelectField } from '../../../../../components/form/hookFields';
import { useCurrentService } from '../../../queries/useCurrentService';
import { ProductFormValues } from '../../schemas/productFormSchema';
import { useCurrencyFormat } from '../../../../../core/globalization/hooks/useCurrencyFormat';
import { WithDisabledPricingFields } from './types';

const PricingTypeSelection = ({ disabled }: Disableable) => {
    const { setValue, watch } = useFormContext<ProductFormValues>();
    return (
        <FormSelectionCardGroupField
            required
            disabled={disabled}
            name="pricing.type"
            label={lt('PricingType')}
            options={[
                {
                    label: lt('Fixed'),
                    name: `pricing.type.fixed`,
                    value: ProductPricing.Absolute,
                },
                {
                    label: lt('Relative'),
                    name: `pricing.type.relative`,
                    value: ProductPricing.Relative,
                },
            ]}
            templateColumns="1fr 1fr"
            type="radio"
            onChange={(value) => {
                switch (value as ProductPricing) {
                    case ProductPricing.Absolute:
                        break;
                    case ProductPricing.Relative: {
                        if (watch('pricing.percentage') === undefined) {
                            setValue('pricing.percentage', 0);
                        }
                        if (watch('pricing.value') === undefined) {
                            setValue('pricing.value', AmountPart.GrossValue);
                        }
                        break;
                    }
                }
            }}
        />
    );
};

export const ProductCurrencyInputField = () => {
    const currencyCode = useWatch({ name: 'pricing.currency' });
    const currencyFormat = useCurrencyFormat(currencyCode);

    return <CurrencyInputField autoFocus required name="pricing.amount" label={lt('Price')} {...currencyFormat} />;
};

const RelativePriceProductSelectField = () => {
    const enterpriseId = useCurrentEnterpriseId();
    const service = useCurrentService();

    const localizeProperty = useLocalizeProperty();

    const query = useRepositoryQuery(
        'products',
        'getAll',
        {
            enterpriseIds: [enterpriseId],
            serviceIds: [service.id],
        },
        {
            select: (response) =>
                response.products
                    ?.filter(({ pricing }) => pricing.discriminator === ProductPricing.Absolute)
                    .map(({ names, id, isDeleted }) =>
                        createOption({
                            label: localizeProperty(names),
                            value: id,
                            inactive: isDeleted,
                        })
                    ) ?? [],
        }
    );

    return (
        <SelectField
            required
            name="pricing.products"
            label={lt('ProductPlural')}
            helper={lt('ProductRelativePricingHint')}
            options={query.data ?? []}
            loader={query.isFetching}
            multiselect
        />
    );
};

export const PricingSection = ({ disabled }: WithDisabledPricingFields) => {
    const pricingType = useWatch({ name: 'pricing.type' });
    return (
        <ContentSection title={lt('Pricing')} spacing="300" contentSpacing="200">
            <PricingTypeSelection disabled={disabled?.pricing} />
            {pricingType === ProductPricing.Absolute ? (
                <Stack spacing="100" fill>
                    <StackItem grow>
                        <ProductCurrencyInputField />
                    </StackItem>
                    <StackItem minWidth="25%">
                        <CurrencySelectField
                            required
                            currencyScope="currentEnterprise"
                            name="pricing.currency"
                            label={lt('Currency')}
                        />
                    </StackItem>
                </Stack>
            ) : null}
            {pricingType === ProductPricing.Relative ? (
                <>
                    <Stack spacing="100" fill>
                        <StackItem grow>
                            <FormPercentageInputField
                                autoFocus
                                required
                                name="pricing.percentage"
                                label={lt('GeneralPercentage')}
                            />
                        </StackItem>
                        <StackItem minWidth="25%">
                            <EnumSelectField
                                defaultValue={AmountPart.GrossValue}
                                enumClass={AmountPart}
                                enumPrefix="AmountPart"
                                required
                                name="pricing.value"
                                label={lt('Value')}
                            />
                        </StackItem>
                    </Stack>
                    <RelativePriceProductSelectField />
                </>
            ) : null}
        </ContentSection>
    );
};
